(function initDogseeVsRawhide() {
  // Follow/release timing for the hover tilt. Kept in JS so the image can ease back with
  // the slower timing before `.is-tilting` is dropped — see the 3D tilt block in the
  // section CSS.
  const TILT_FOLLOW = "0.12s";
  const TILT_RELEASE = "0.5s";
  const TILT_RELEASE_MS = 520;

  // Tilts the product PNG away from the pointer. The rotation lands on the <img> itself
  // (see section-dogsee-vs-rawhide.css) — never on an ancestor — so the image keeps
  // blending its black studio background away. Returns a teardown function.
  function armHoverTilt(section) {
    const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const surfaces = [...section.querySelectorAll("[data-dvr-tilt]")].filter(
      (surface) => !(canHover && surface.hasAttribute("data-dvr-zoom")),
    );
    if (!surfaces.length) return null;

    if (reduceMotion) return null;
    if (!canHover) return armTapTilt(surfaces);

    const unbinds = surfaces.map((surface) => {
      // The whole panel is the hit area, so the chew reacts without pixel-perfect aim.
      const hitArea = surface.closest(".section-dogsee-vs-rawhide__panel") || surface;

      let frame = 0;
      let releaseTimer = 0;
      let nextX = 0;
      let nextY = 0;

      const paint = () => {
        frame = 0;
        surface.style.setProperty("--dvr-tilt-fx", nextX.toFixed(3));
        surface.style.setProperty("--dvr-tilt-fy", nextY.toFixed(3));
      };

      const release = () => {
        if (!surface.classList.contains("is-tilting")) return;
        if (frame) {
          window.cancelAnimationFrame(frame);
          frame = 0;
        }
        nextX = 0;
        nextY = 0;
        // Ease back with the slower release timing, then drop the tilt state.
        surface.style.setProperty("--dvr-tilt-follow", TILT_RELEASE);
        surface.style.setProperty("--dvr-tilt-fx", "0");
        surface.style.setProperty("--dvr-tilt-fy", "0");
        window.clearTimeout(releaseTimer);
        releaseTimer = window.setTimeout(() => {
          releaseTimer = 0;
          surface.classList.remove("is-tilting");
        }, TILT_RELEASE_MS);
      };

      const onMove = (event) => {
        const rect = surface.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const clamp = (value) => Math.max(-1, Math.min(1, value));
        nextX = clamp(((event.clientX - rect.left) / rect.width) * 2 - 1);
        nextY = clamp(((event.clientY - rect.top) / rect.height) * 2 - 1);
        window.clearTimeout(releaseTimer);
        releaseTimer = 0;
        surface.style.setProperty("--dvr-tilt-follow", TILT_FOLLOW);
        surface.classList.add("is-tilting");
        if (!frame) frame = window.requestAnimationFrame(paint);
      };

      hitArea.addEventListener("pointerenter", onMove);
      hitArea.addEventListener("pointermove", onMove);
      hitArea.addEventListener("pointerleave", release);
      hitArea.addEventListener("pointercancel", release);
      // Scrolling slides the product out from under a parked cursor — drop the tilt.
      window.addEventListener("scroll", release, { passive: true });

      return () => {
        if (frame) window.cancelAnimationFrame(frame);
        window.clearTimeout(releaseTimer);
        releaseTimer = 0;
        hitArea.removeEventListener("pointerenter", onMove);
        hitArea.removeEventListener("pointermove", onMove);
        hitArea.removeEventListener("pointerleave", release);
        hitArea.removeEventListener("pointercancel", release);
        window.removeEventListener("scroll", release);
        surface.classList.remove("is-tilting");
        surface.style.removeProperty("--dvr-tilt-fx");
        surface.style.removeProperty("--dvr-tilt-fy");
        surface.style.removeProperty("--dvr-tilt-follow");
      };
    });

    return () => {
      unbinds.forEach((unbind) => unbind());
    };
  }

  // Round magnifier that follows the pointer over the chew and its callout text. The lens
  // holds a scaled copy of the media box (photo + callouts), so text stays sharp. The copy
  // sits on the lens's own dark base and the PNG blends with `lighten`, so the black
  // studio background disappears inside the lens the same way it does on the page.
  const ZOOM = 2.2;

  function largeSource(img) {
    let src = img.currentSrc || img.src;
    try {
      const url = new URL(src, window.location.href);
      if (url.searchParams.has("width")) {
        url.searchParams.set("width", "1600");
        src = url.toString();
      }
    } catch (error) {
      // Keep the rendered source.
    }
    return src;
  }

  function armHoverZoom(section) {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return null;
    const surfaces = [...section.querySelectorAll("[data-dvr-zoom]")];
    if (!surfaces.length) return null;

    const unbinds = surfaces.map((surface) => {
      const img = surface.querySelector(".section-dogsee-vs-rawhide__image");
      if (!img) return () => {};

      const lens = document.createElement("span");
      lens.className = "section-dogsee-vs-rawhide__lens";
      lens.setAttribute("aria-hidden", "true");
      const stage = document.createElement("span");
      stage.className = "section-dogsee-vs-rawhide__lens-stage";
      lens.appendChild(stage);
      surface.appendChild(lens);

      let built = false;
      const build = () => {
        built = true;
        const photo = document.createElement("img");
        photo.className = "section-dogsee-vs-rawhide__lens-photo";
        photo.alt = "";
        photo.decoding = "async";
        photo.src = largeSource(img);
        stage.appendChild(photo);
        surface.querySelectorAll(".section-dogsee-vs-rawhide__callout").forEach((callout) => {
          stage.appendChild(callout.cloneNode(true));
        });
      };

      let frame = 0;
      let point = null;

      const paint = () => {
        frame = 0;
        if (!point) return;
        const rect = surface.getBoundingClientRect();
        const x = point.x - rect.left;
        const y = point.y - rect.top;
        const inside = x >= 0 && y >= 0 && x <= rect.width && y <= rect.height;
        lens.classList.toggle("is-on", inside);
        surface.classList.toggle("is-zooming", inside);
        if (!inside) return;
        if (!built) build();
        const size = lens.offsetWidth;
        const photo = stage.firstElementChild;
        stage.style.width = `${surface.clientWidth}px`;
        stage.style.height = `${surface.clientHeight}px`;
        if (photo) {
          photo.style.left = `${img.offsetLeft}px`;
          photo.style.top = `${img.offsetTop}px`;
          photo.style.width = `${img.offsetWidth}px`;
          photo.style.height = `${img.offsetHeight}px`;
        }
        lens.style.left = `${x}px`;
        lens.style.top = `${y}px`;
        stage.style.transform = `translate(${size / 2 - x * ZOOM}px, ${size / 2 - y * ZOOM}px) scale(${ZOOM})`;
      };

      const onMove = (event) => {
        point = { x: event.clientX, y: event.clientY };
        if (!frame) frame = window.requestAnimationFrame(paint);
      };
      const hide = () => {
        point = null;
        lens.classList.remove("is-on");
        surface.classList.remove("is-zooming");
      };

      surface.addEventListener("pointermove", onMove);
      surface.addEventListener("pointerleave", hide);
      window.addEventListener("scroll", hide, { passive: true });

      return () => {
        if (frame) window.cancelAnimationFrame(frame);
        surface.removeEventListener("pointermove", onMove);
        surface.removeEventListener("pointerleave", hide);
        window.removeEventListener("scroll", hide);
        surface.classList.remove("is-zooming");
        lens.remove();
      };
    });

    return () => {
      unbinds.forEach((unbind) => unbind());
    };
  }

  // Touch screens have no hover, so a tap tips the chew towards the tap point and
  // it settles back into the idle sway.
  function armTapTilt(surfaces) {
    const unbinds = surfaces.map((surface) => {
      let releaseTimer = 0;
      let settleTimer = 0;

      const onTap = (event) => {
        const rect = surface.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const clamp = (value) => Math.max(-1, Math.min(1, value));
        const fx = clamp(((event.clientX - rect.left) / rect.width) * 2 - 1);
        const fy = clamp(((event.clientY - rect.top) / rect.height) * 2 - 1);
        window.clearTimeout(releaseTimer);
        window.clearTimeout(settleTimer);
        surface.style.setProperty("--dvr-tilt-follow", "0.25s");
        surface.style.setProperty("--dvr-tilt-fx", fx.toFixed(3));
        surface.style.setProperty("--dvr-tilt-fy", fy.toFixed(3));
        surface.classList.add("is-tilting");
        releaseTimer = window.setTimeout(() => {
          surface.style.setProperty("--dvr-tilt-follow", TILT_RELEASE);
          surface.style.setProperty("--dvr-tilt-fx", "0");
          surface.style.setProperty("--dvr-tilt-fy", "0");
          settleTimer = window.setTimeout(() => {
            surface.classList.remove("is-tilting");
          }, TILT_RELEASE_MS);
        }, 650);
      };

      surface.addEventListener("pointerdown", onTap);

      return () => {
        window.clearTimeout(releaseTimer);
        window.clearTimeout(settleTimer);
        surface.removeEventListener("pointerdown", onTap);
        surface.classList.remove("is-tilting");
        surface.style.removeProperty("--dvr-tilt-fx");
        surface.style.removeProperty("--dvr-tilt-fy");
        surface.style.removeProperty("--dvr-tilt-follow");
      };
    });

    return () => {
      unbinds.forEach((unbind) => unbind());
    };
  }

  function setup(root) {
    if (!root || typeof root.querySelectorAll !== "function") return;

    const sections = root.matches?.("[data-dogsee-vs-rawhide]")
      ? [root]
      : [...root.querySelectorAll("[data-dogsee-vs-rawhide]")];

    sections.forEach((section) => {
      if (section.dataset.ready === "true") return;
      section.dataset.ready = "true";

      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const animateEnabled = section.dataset.animate !== "false";

      const reveal = () => {
        section.classList.add("is-visible");
      };

      // Hover tilt is armed immediately — the CSS only applies it once `.is-visible`
      // is set, and listeners only react to real pointer movement, so the entrance
      // animation is unaffected.
      const tiltTeardown = armHoverTilt(section);
      const zoomTeardown = armHoverZoom(section);
      const disableTilt = () => {
        if (typeof tiltTeardown === "function") tiltTeardown();
        if (typeof zoomTeardown === "function") zoomTeardown();
      };

      if (!animateEnabled || reduceMotion || !("IntersectionObserver" in window)) {
        reveal();
        section._dvrCleanup = () => {
          disableTilt();
          section.classList.remove("is-reveal-armed");
          section.dataset.ready = "false";
        };
        return;
      }

      section.classList.add("is-reveal-armed");

      let played = false;
      let safetyTimer = null;
      const play = () => {
        if (played) return;
        played = true;
        reveal();
        if (safetyTimer) {
          window.clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        if (observer) observer.disconnect();
      };

      safetyTimer = window.setTimeout(play, 2800);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) play();
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -5% 0px" }
      );

      observer.observe(section);
      section._dvrCleanup = () => {
        disableTilt();
        if (safetyTimer) {
          window.clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        observer.disconnect();
        section.classList.remove("is-reveal-armed");
        section.dataset.ready = "false";
      };

      requestAnimationFrame(() => {
        const rect = section.getBoundingClientRect();
        const view = window.innerHeight || 1;
        if (rect.top < view * 0.85 && rect.bottom > view * 0.15) play();
      });
    });
  }

  const start = () => setup(document);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  document.addEventListener("shopify:section:load", (event) => setup(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target;
    if (!root || typeof root.querySelectorAll !== "function") return;
    const sections = root.matches?.("[data-dogsee-vs-rawhide]")
      ? [root]
      : [...root.querySelectorAll("[data-dogsee-vs-rawhide]")];
    sections.forEach((section) => {
      if (typeof section._dvrCleanup === "function") section._dvrCleanup();
    });
  });
})();
